import * as client from "../../../client";
import {
  AttachmentMapping,
  attachmentsUiTypeMap,
  effectiveMapping,
  parseUploadedAt,
} from "./mapping";

// Classifications whose relevance mirrors an explicit enabled/selected control
// elsewhere in the app (SOV tab, loss-run sources, policy form selection)
// rather than upload recency.
const SOV_TYPES: ReadonlySet<AttachmentMapping> = new Set<AttachmentMapping>([
  "sov_spreadsheet",
  "bound_sov",
]);
const POLICY_FORM_TYPES: ReadonlySet<AttachmentMapping> =
  new Set<AttachmentMapping>([
    "policy_document",
    "manuscript_form",
    "final_manuscript_form",
    "specs_sheet",
  ]);

const NEVER_RELEVANT: ReadonlySet<AttachmentMapping> =
  new Set<AttachmentMapping>(["none", "record_keeping", "screenshot"]);

export type RelevanceSources = {
  sovs: readonly { filename: string; is_enabled: boolean }[];
  lossRun: client.LossRunInfo | undefined;
  selectedPolicyFormName: string | null;
  uploadedAtByName: Record<string, string | null>;
};

const uploadMillis = (
  attachment: client.Attachment,
  sources: RelevanceSources
): number =>
  parseUploadedAt(sources.uploadedAtByName[attachment.name])?.getTime() ?? 0;

/**
 * Which attachments are currently "relevant": the ones the report is actually
 * using (the enabled SOV, enabled loss-run sources, the selected policy form)
 * plus the most recently uploaded attachment of every other classification.
 * Returns a map from attachment name to a human-readable reason, suitable for
 * a tooltip. Attachments without a recorded upload time (email and legacy
 * files) count as oldest; ties for newest are all marked relevant.
 */
export const computeAttachmentRelevance = (
  attachments: readonly client.Attachment[],
  sources: RelevanceSources
): Map<string, string> => {
  const relevance = new Map<string, string>();

  // A SOV parsed from a spreadsheet sheet is named "<file> - <sheet>" (see
  // backend parse_sov.py), so match the attachment as a prefix, not exactly.
  const enabledSovFilenames = sources.sovs
    .filter((sov) => sov.is_enabled)
    .map((sov) => sov.filename);
  const hasEnabledSov = (attachmentName: string): boolean =>
    enabledSovFilenames.some(
      (filename) =>
        filename === attachmentName ||
        filename.startsWith(`${attachmentName} - `)
    );
  const enabledLossRunFilenames = new Set(
    Object.values(sources.lossRun?.enabled_by_source ?? {})
      .filter((info) => info.enabled)
      .map((info) => info.source_details.filename)
  );

  const latestCandidates = new Map<AttachmentMapping, client.Attachment[]>();

  for (const attachment of attachments) {
    const mapping = effectiveMapping(attachment);
    if (NEVER_RELEVANT.has(mapping)) {
      continue;
    }
    if (SOV_TYPES.has(mapping)) {
      if (hasEnabledSov(attachment.name)) {
        relevance.set(attachment.name, "Enabled SOV");
      }
    } else if (mapping === "loss_run") {
      if (enabledLossRunFilenames.has(attachment.name)) {
        relevance.set(attachment.name, "Enabled loss run source");
      }
    } else if (POLICY_FORM_TYPES.has(mapping)) {
      if (attachment.name === sources.selectedPolicyFormName) {
        relevance.set(attachment.name, "Selected policy form");
      }
    } else {
      const group = latestCandidates.get(mapping) ?? [];
      group.push(attachment);
      latestCandidates.set(mapping, group);
    }
  }

  for (const [mapping, group] of latestCandidates) {
    const newestMillis = Math.max(
      ...group.map((attachment) => uploadMillis(attachment, sources))
    );
    for (const attachment of group) {
      if (uploadMillis(attachment, sources) === newestMillis) {
        relevance.set(
          attachment.name,
          `Latest ${attachmentsUiTypeMap[mapping]}`
        );
      }
    }
  }

  return relevance;
};
